import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient, FeedbackItem } from '../lib/api'
import { ChevronLeftIcon, ChevronRightIcon, XIcon, CopyIcon, CheckIcon, ClockIcon, FileTextIcon, ChevronDownIcon } from 'lucide-react'
import { VscThumbsupFilled, VscThumbsdownFilled } from 'react-icons/vsc'

// ── Shared rating badge ───────────────────────────────────────────────────────

function RatingBadge({ isDown, size = 'sm' }: { isDown: boolean; size?: 'sm' | 'md' }) {
  const padClass = size === 'md' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${padClass}`}
      style={{ backgroundColor: isDown ? '#fee2e2' : '#dcfce7' }}
    >
      {isDown
        ? <VscThumbsdownFilled className="text-red-500" style={{ fontSize: size === 'md' ? 14 : 12 }} />
        : <VscThumbsupFilled className="text-green-600" style={{ fontSize: size === 'md' ? 14 : 12 }} />
      }
      <span className={isDown ? 'text-red-500' : 'text-green-600'}>
        {isDown ? 'Thumbs Down' : 'Thumbs Up'}
      </span>
    </span>
  )
}

// ── Detail modal ──────────────────────────────────────────────────────────────

function FeedbackDetailModal({ item, onClose }: { item: FeedbackItem; onClose: () => void }) {
  const isDown = item.rating === 'thumbs_down'
  const [copied, setCopied] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (el) setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4)
  }, [item.original_answer])

  const handleCopy = () => {
    navigator.clipboard.writeText(item.original_answer)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const lineCount = item.original_answer.split('\n').length

  const idFields = [
    { label: 'Feedback ID', value: item.id },
    { label: 'Conversation ID', value: item.conversation_id },
    { label: 'Session ID', value: item.session_id },
    { label: 'Message ID', value: item.message_id },
  ]

  const headerGradient = isDown
    ? 'linear-gradient(135deg, #fff5f5 0%, #ffffff 60%)'
    : 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%)'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 rounded-t-2xl"
          style={{ background: headerGradient }}
        >
          <div className="flex items-center gap-2.5">
            <RatingBadge isDown={isDown} size="md" />
            {item.source_type && (
              <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-white/80 border border-gray-200 text-gray-500 shadow-sm">
                {item.source_type}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/80 transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto">

          {/* IDs */}
          <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 border border-gray-100">
            {idFields.map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                <p className="text-xs font-mono text-gray-700 break-all leading-relaxed">
                  {value ?? <span className="text-gray-300">—</span>}
                </p>
              </div>
            ))}
          </div>

          {/* Question */}
          <div className="pl-4 border-l-4 border-primary-500 py-0.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Question</p>
            <p className="text-sm font-semibold text-gray-900 leading-relaxed">{item.question}</p>
          </div>

          {/* AI Answer — dark code block */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">AI Answer</p>
            <div className="rounded-xl overflow-hidden shadow-lg" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
              {/* Code block header bar */}
              <div className="flex items-center justify-between px-4 py-2.5" style={{ background: 'linear-gradient(90deg, #1a1a2e 0%, #16213e 100%)' }}>
                <div className="flex items-center gap-2">
                  <FileTextIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#6272a4' }} />
                  <span className="text-xs font-medium tracking-wide" style={{ color: '#6272a4' }}>markdown</span>
                  <span className="text-xs" style={{ color: '#44475a' }}>·</span>
                  <span className="text-xs font-medium" style={{ color: '#44475a' }}>
                    {lineCount} {lineCount === 1 ? 'line' : 'lines'}
                  </span>
                </div>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all"
                  style={{
                    background: copied ? 'rgba(40, 200, 64, 0.15)' : 'rgba(255,255,255,0.08)',
                    color: copied ? '#28c840' : '#8892b0',
                    border: `1px solid ${copied ? 'rgba(40,200,64,0.3)' : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  {copied
                    ? <><CheckIcon className="h-3 w-3" /><span>Copied!</span></>
                    : <><CopyIcon className="h-3 w-3" /><span>Copy</span></>
                  }
                </button>
              </div>
              {/* Code body — min/max height, scrolls internally */}
              <div className="relative">
                <div
                  ref={scrollRef}
                  onScroll={handleScroll}
                  className="min-h-52 max-h-96 overflow-y-auto"
                  style={{ background: 'linear-gradient(180deg, #1e1e2e 0%, #1a1a2a 100%)' }}
                >
                  <pre
                    className="text-sm leading-relaxed whitespace-pre-wrap font-mono px-5 py-4"
                    style={{ color: '#cdd6f4' }}
                  >
                    {item.original_answer}
                  </pre>
                </div>
                {/* Scroll-to-bottom indicator */}
                {!isAtBottom && (
                  <div
                    className="absolute bottom-0 left-0 right-0 flex justify-center pb-2 pt-8 cursor-pointer"
                    style={{ background: 'linear-gradient(to top, #1e1e2e 0%, transparent 100%)' }}
                    onClick={() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })}
                  >
                    <ChevronDownIcon className="h-4 w-4 animate-bounce" style={{ color: '#6272a4' }} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Thumbs-down specific fields */}
          {isDown && (
            <>
              {/* Feedback Categories */}
              {item.feedback_categories && item.feedback_categories.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
                    Feedback Categories
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {item.feedback_categories.map((cat) => (
                      <span
                        key={cat}
                        className="inline-flex px-2.5 py-1 text-xs rounded-full font-medium border shadow-sm"
                        style={{ backgroundColor: '#fff1f1', borderColor: '#fecaca', color: '#ef4444' }}
                      >
                        {cat.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* User Comments */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5">User Comments</p>
                <div className="bg-gray-50 rounded-xl px-4 py-3.5 border border-gray-100">
                  {item.feedback_details
                    ? <p className="text-sm text-gray-700 leading-relaxed">{item.feedback_details}</p>
                    : <p className="text-sm text-gray-400 italic">No comments provided</p>
                  }
                </div>
              </div>

              {/* Suggested Answer */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5">Suggested Answer</p>
                <div
                  className="rounded-xl px-4 py-3.5 border"
                  style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a' }}
                >
                  {item.suggested_answer
                    ? <p className="text-sm text-gray-700 leading-relaxed">{item.suggested_answer}</p>
                    : <p className="text-sm text-amber-400 italic">No suggestion provided</p>
                  }
                </div>
              </div>
            </>
          )}

          {/* Submitted date */}
          <div className="pt-1 border-t border-gray-100 flex items-center gap-1.5">
            <ClockIcon className="h-3.5 w-3.5 text-gray-300" />
            <p className="text-xs text-gray-400">
              Submitted{' '}
              {new Date(item.created_at).toLocaleString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main tab component ────────────────────────────────────────────────────────

interface TenantFeedbackTabProps {
  tenantId: string
}

const PAGE_SIZE = 10

export default function TenantFeedbackTab({ tenantId }: TenantFeedbackTabProps) {
  const [selected, setSelected] = useState<FeedbackItem | null>(null)
  const [page, setPage] = useState(1)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['feedback', tenantId],
    queryFn: () => apiClient.getFeedback({ tenant_id: tenantId, limit: 100 }),
    enabled: !!tenantId,
  })

  const items: FeedbackItem[] = data?.items ?? []

  useEffect(() => { setPage(1) }, [tenantId])

  const totalPages = Math.ceil(items.length / PAGE_SIZE)
  const pagedItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  if (isLoading) {
    return <div className="py-16 text-center text-sm text-gray-400">Loading feedback…</div>
  }

  if (isError) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-red-500 mb-3">{(error as Error).message}</p>
        <button onClick={() => refetch()} className="text-sm text-primary-600 hover:underline">
          Retry
        </button>
      </div>
    )
  }

  return (
    <>
      {/* ── Summary bar ── */}
      <div className="grid grid-cols-3 divide-x divide-gray-100 bg-white border border-gray-100 rounded-xl overflow-hidden mb-6 shadow-sm">
        {[
          { label: 'Total Feedback', value: data?.total ?? 0, color: 'text-gray-900' },
          { label: 'Thumbs Up', value: items.filter((i) => i.rating === 'thumbs_up').length, color: 'text-green-600' },
          { label: 'Thumbs Down', value: items.filter((i) => i.rating === 'thumbs_down').length, color: 'text-red-500' },
        ].map((stat, idx) => (
          <div key={idx} className="px-6 py-5">
            <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-gray-500 mt-1 font-medium uppercase tracking-wide">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* ── Card list ── */}
      <div className="space-y-2.5">
        {items.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-xl px-6 py-16 text-center">
            <p className="text-sm text-gray-400">No feedback yet</p>
          </div>
        ) : (
          pagedItems.map((item) => {
            const isDown = item.rating === 'thumbs_down'
            return (
              <div
                key={item.id}
                className="bg-white border border-gray-100 rounded-xl px-5 py-4 hover:border-gray-200 hover:-translate-y-0.5 transition-all duration-150 cursor-default"
              >
                {/* Top row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <RatingBadge isDown={isDown} />
                    {item.source_type && (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                        {item.source_type}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5 font-medium">
                    {new Date(item.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>

                {/* Question */}
                <p className="text-sm font-semibold text-gray-900 mb-2 leading-snug">{item.question}</p>

                {/* Answer preview */}
                <div className="bg-gray-50 rounded-lg px-3 py-2.5 mb-3 border border-gray-100">
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{item.original_answer}</p>
                </div>

                {/* Categories (thumbs down only) */}
                {isDown && item.feedback_categories && item.feedback_categories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {item.feedback_categories.map((cat) => (
                      <span
                        key={cat}
                        className="inline-flex px-2 py-0.5 text-xs rounded-full font-medium border"
                        style={{ backgroundColor: '#fee2e2', borderColor: '#fecaca', color: '#ef4444' }}
                      >
                        {cat.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2.5 border-t border-gray-50">
                  <button
                    onClick={() => setSelected(item)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 border border-primary-100 rounded-lg hover:bg-primary-100 hover:border-primary-200 transition-colors"
                  >
                    View details
                    <span className="text-primary-400">→</span>
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-5 px-1">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page <span className="font-semibold text-gray-800">{page}</span> of{' '}
            <span className="font-semibold text-gray-800">{totalPages}</span>
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            Next
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Detail modal ── */}
      {selected && <FeedbackDetailModal item={selected} onClose={() => setSelected(null)} />}
    </>
  )
}
